/**
 * StaticServerConnector — serve a directory of static files over HTTP.
 * Category: service
 *
 * Built on `node:http` + `node:fs`, so it runs under both Node and Bun.
 */

import { createReadStream, type Stats } from "node:fs";
import { stat } from "node:fs/promises";
import { createServer, type Server as HttpServer, type ServerResponse } from "node:http";
import { join, resolve, sep } from "node:path";
import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import {
  AbstractConnector,
  createWorktree,
  findGitRoot,
  PortPool,
} from "@skaile/workspaces/connectors";
import { withFsDeadline } from "@skaile/workspaces/core";
import mime from "mime";

interface StaticInstance {
  id: string;
  port: number;
  dir: string;
  server: HttpServer;
  ref?: string;
  worktreePath?: string;
  worktreeCleanup?: () => Promise<void>;
  startedAt: string;
  status: "healthy" | "stopped";
}

interface StaticServerState {
  instances: Map<string, StaticInstance>;
  pool: PortPool;
  repoRoot: string | null;
  worktreeDir: string;
}

const DEFAULT_PORT_RANGE: [number, number] = [14000, 14099];
const DEFAULT_WORKTREE_DIR = "/tmp/skaile-static-worktrees";

/**
 * Connector for serving static file directories over HTTP.
 * Supports multiple concurrent instances, port allocation, and optional git worktree isolation.
 * @docLink packages/factory-assets/concepts#static-server-connector
 */
export class StaticServerConnector extends AbstractConnector {
  readonly name = "static-server";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    this.log.info("connect", { id: declaration.id });
    const range = (declaration.options?.port_range as [number, number]) ?? DEFAULT_PORT_RANGE;
    const worktreeDir = String(declaration.options?.worktree_dir ?? DEFAULT_WORKTREE_DIR);
    let repoRoot: string | null = null;
    try {
      repoRoot = await findGitRoot(process.cwd());
    } catch {}

    return this.makeHandle(declaration, {
      instances: new Map(),
      pool: new PortPool(range),
      repoRoot,
      worktreeDir,
    } as StaticServerState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    try {
      await this._stopAll(handle);
    } finally {
      handle.status = "disconnected";
    }
  }

  override async onSessionClose(handle: ConnectorHandle): Promise<string | undefined> {
    await this._stopAll(handle);
    return undefined;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _stopAll(handle: ConnectorHandle): Promise<void> {
    const state = handle.state as StaticServerState;
    for (const [, instance] of state.instances) {
      stopHttpServer(instance.server);
      instance.status = "stopped";
      if (instance.worktreeCleanup) {
        try {
          await instance.worktreeCleanup();
        } catch {}
      }
      state.pool.release(instance.port);
    }
    state.instances.clear();
  }

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "serve",
        description: "Serve a directory of static files over HTTP.",
        accessLevel: "write",
        args: [
          { name: "dir", type: "string", required: true, description: "Directory to serve" },
          { name: "port", type: "number", required: false, description: "Explicit port" },
          { name: "label", type: "string", required: false, description: "Instance label" },
          { name: "ref", type: "string", required: false, description: "Git ref for worktree" },
        ],
      },
      {
        name: "stop",
        description: "Stop a static server.",
        accessLevel: "write",
        args: [{ name: "instance", type: "string", required: true, description: "Instance ID" }],
      },
      {
        name: "status",
        description: "Get instance status.",
        accessLevel: "read",
        args: [
          {
            name: "instance",
            type: "string",
            required: false,
            description: "Instance ID (omit for all)",
          },
        ],
      },
      {
        name: "list_instances",
        description: "List all active instances.",
        accessLevel: "read",
        args: [],
      },
      { name: "free_port", description: "Reserve a free port.", accessLevel: "read", args: [] },
    ];
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const state = handle.state as StaticServerState;
    if ((operation === "serve" || operation === "stop") && handle.access === "read-only") {
      throw new Error(`StaticServerConnector: "${operation}" requires read-write access`);
    }

    switch (operation) {
      case "serve":
        return this._opServe(state, args);
      case "stop":
        return this._opStop(state, args);
      case "status":
        return this._opStatus(state, args);
      case "list_instances":
        return this._opListInstances(state);
      case "free_port":
        return this._opFreePort(state);
      default:
        throw new Error(`StaticServerConnector: unknown operation "${operation}"`);
    }
  }

  private async _opServe(state: StaticServerState, args: Record<string, unknown>): Promise<string> {
    let dir = args.dir ? String(args.dir) : "";
    if (!dir && !args.ref) throw new Error("serve requires a dir or ref");
    const label = args.label ? String(args.label) : `static-${Date.now()}`;
    const port = args.port ? Number(args.port) : await state.pool.allocate();
    if (args.port) state.pool.reserve(port);

    let worktreePath: string | undefined;
    let worktreeCleanup: (() => Promise<void>) | undefined;
    let ref: string | undefined;

    if (args.ref) {
      if (!state.repoRoot) throw new Error("ref-based serving requires a git repository");
      ref = String(args.ref);
      const wt = await createWorktree({
        repoRoot: state.repoRoot,
        ref,
        baseDir: state.worktreeDir,
        label,
        owner: `static:${label}`,
      });
      worktreePath = wt.path;
      worktreeCleanup = wt.cleanup;
      dir = dir ? join(wt.path, dir) : wt.path;
    }

    const serveDir = dir;
    const server = createServer((req, res) => {
      // serveStaticFile is async now; a rejection would otherwise be an
      // unhandled rejection that leaves the socket open forever.
      void serveStaticFile(serveDir, req.url ?? "/", res).catch(() => {
        if (!res.headersSent) res.writeHead(500, { "content-type": "text/plain" });
        res.end();
      });
    });
    server.listen(port);

    state.instances.set(label, {
      id: label,
      port,
      dir: serveDir,
      server,
      ref,
      worktreePath,
      worktreeCleanup,
      startedAt: new Date().toISOString(),
      status: "healthy",
    });
    this.emitChange?.({ path: label, action: "create", source: "operation" });
    return JSON.stringify({ instance_id: label, port, url: `http://localhost:${port}` });
  }

  private async _opStop(state: StaticServerState, args: Record<string, unknown>): Promise<string> {
    const id = String(args.instance ?? "");
    const instance = state.instances.get(id);
    if (!instance) throw new Error(`Instance "${id}" not found`);
    stopHttpServer(instance.server);
    instance.status = "stopped";
    state.pool.release(instance.port);
    if (instance.worktreeCleanup) {
      try {
        await instance.worktreeCleanup();
      } catch {}
    }
    state.instances.delete(id);
    this.emitChange?.({ path: id, action: "delete", source: "operation" });
    return JSON.stringify({ stopped: true });
  }

  private _opStatus(state: StaticServerState, args: Record<string, unknown>): string {
    if (args.instance) {
      const inst = state.instances.get(String(args.instance));
      if (!inst) throw new Error(`Instance "${args.instance}" not found`);
      return JSON.stringify(instanceToStatus(inst));
    }
    return JSON.stringify([...state.instances.values()].map(instanceToStatus));
  }

  private _opListInstances(state: StaticServerState): string {
    return JSON.stringify(
      [...state.instances.values()].map((i) => ({
        id: i.id,
        port: i.port,
        dir: i.dir,
        url: `http://localhost:${i.port}`,
        ref: i.ref,
        status: i.status,
        started_at: i.startedAt,
      })),
    );
  }

  private async _opFreePort(state: StaticServerState): Promise<string> {
    return JSON.stringify({ port: await state.pool.allocate() });
  }

  private _describeSkill(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    return {
      id: declaration.id,
      name: "Static File Server",
      description: "Serve directories of static files over HTTP.",
      category: "service",
      access: handle.access,
      toolNames: this._describeOperations(handle).map((o) => o.name),
    };
  }
}

/**
 * `stat` under a deadline, collapsing every failure — missing, unreadable, or
 * an unresponsive mount — to `undefined`. All three mean the same thing here:
 * there is nothing servable at this path.
 */
async function statOrNull(filePath: string): Promise<Stats | undefined> {
  try {
    return await withFsDeadline(`stat ${filePath}`, () => stat(filePath));
  } catch {
    return undefined;
  }
}

/**
 * Serve a single file from `rootDir` for the given request URL. Rejects paths
 * that escape `rootDir` (path traversal), 404s missing files, and resolves
 * directory requests to `index.html`.
 *
 * The path is derived from the request URL, so this is the hottest untrusted
 * filesystem entry point in the connector. It used to issue four synchronous
 * `existsSync`/`statSync` calls per request; if `rootDir` sat on a network
 * mount — which the containment guard says nothing about, it only proves the
 * path stays *inside* `rootDir` — a single request could park the event loop.
 * Now: one deadline-bounded async `stat` per candidate path.
 */
async function serveStaticFile(
  rootDir: string,
  reqUrl: string,
  res: ServerResponse,
): Promise<void> {
  const root = resolve(rootDir);
  const pathname = decodeURIComponent(new URL(reqUrl, "http://localhost").pathname);
  let filePath = resolve(root, `.${pathname}`);
  // Containment guard — never serve a file outside the configured directory.
  if (filePath !== root && !filePath.startsWith(root + sep)) {
    res.writeHead(403, { "content-type": "text/plain" });
    res.end("Forbidden");
    return;
  }
  // A trailing slash is a directory request by definition — no stat needed to
  // learn that, so the index.html rewrite happens before touching the disk.
  let stats: Stats | undefined;
  if (pathname.endsWith("/")) {
    filePath = join(filePath, "index.html");
    stats = await statOrNull(filePath);
  } else {
    stats = await statOrNull(filePath);
    if (stats?.isDirectory()) {
      filePath = join(filePath, "index.html");
      stats = await statOrNull(filePath);
    }
  }
  if (!stats?.isFile()) {
    res.writeHead(404, { "content-type": "text/plain" });
    res.end("Not Found");
    return;
  }
  res.writeHead(200, { "content-type": mime.getType(filePath) ?? "application/octet-stream" });
  const stream = createReadStream(filePath);
  stream.on("error", () => {
    if (!res.headersSent) res.writeHead(500, { "content-type": "text/plain" });
    res.end();
  });
  stream.pipe(res);
}

/** Stop an HTTP server, terminating any in-flight connections immediately. */
function stopHttpServer(server: HttpServer): void {
  server.closeAllConnections();
  server.close();
}

function instanceToStatus(instance: StaticInstance) {
  return {
    id: instance.id,
    port: instance.port,
    dir: instance.dir,
    url: `http://localhost:${instance.port}`,
    ref: instance.ref,
    status: instance.status,
    started_at: instance.startedAt,
    uptime_ms: Date.now() - new Date(instance.startedAt).getTime(),
  };
}

/**
 * Creates a new StaticServerConnector instance.
 * @returns Configured StaticServerConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#static-server-connector-factory
 */
export function createConnector(): StaticServerConnector {
  return new StaticServerConnector();
}
